import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  Supplier,
  SupplierStats,
  CreateSupplierData,
  UpdateSupplierData,
  SupplierProduct,
  SupplierUser,
} from '@/types/supplier'
import { SupplierStatus } from '@/types/supplier'
import { supplierApi } from '@/modules/supplier/api/supplier.api'
import { supplierProductApi } from '@/modules/supplier/api/supplierProduct.api'
import { useSupplierUserAPI } from '@/modules/supplier/api/supplierUser.api'

export const useSupplierStore = defineStore('supplier', () => {
  // Состояние
  const suppliers = ref<Supplier[]>([])
  const currentSupplier = ref<Supplier | null>(null)
  const supplierProducts = ref<SupplierProduct[]>([])
  const supplierUsers = ref<SupplierUser[]>([])
  const stats = ref<SupplierStats | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Пагинация
  const currentPage = ref(1)
  const itemsPerPage = ref(20)
  const totalCount = ref(0)

  // Фильтры
  const searchQuery = ref('')
  const selectedStatus = ref<SupplierStatus | null>(null)
  const selectedCity = ref<string | null>(null)
  const minRating = ref<number | null>(null)
  const featuredOnly = ref(false)

  // Сортировка
  const sortBy = ref('rating')
  const sortOrder = ref<'asc' | 'desc'>('desc')

  // Геттеры
  const filteredSuppliers = computed(() => {
    return suppliers.value
  })

  const paginationInfo = computed(() => {
    return {
      currentPage: currentPage.value,
      totalPages: Math.ceil(totalCount.value / itemsPerPage.value),
      totalItems: totalCount.value,
      itemsPerPage: itemsPerPage.value,
    }
  })

  const pendingSuppliers = computed(() => {
    return suppliers.value.filter((supplier) => supplier.status === SupplierStatus.PENDING)
  })

  const approvedSuppliers = computed(() => {
    return suppliers.value.filter((supplier) => supplier.status === SupplierStatus.APPROVED)
  })

  const activeSuppliers = computed(() => {
    return suppliers.value.filter((supplier) => supplier.isActive)
  })

  const featuredSuppliers = computed(() => {
    return suppliers.value.filter((supplier) => supplier.isFeatured)
  })

  // Действия
  async function fetchSuppliers(scope: 'public' | 'admin' = 'public') {
    loading.value = true
    error.value = null

    try {
      const api = supplierApi()
      const filters: {
        search?: string
        status?: SupplierStatus[]
        city?: string
        rating?: number
        isFeatured?: boolean
      } = {}
      if (searchQuery.value) filters.search = searchQuery.value
      if (selectedStatus.value) filters.status = [selectedStatus.value]
      if (selectedCity.value) filters.city = selectedCity.value
      if (minRating.value !== null) filters.rating = minRating.value
      if (featuredOnly.value) filters.isFeatured = true

      const sort = {
        by: sortBy.value as 'rating' | 'name' | 'createdAt' | 'productCount',
        order: sortOrder.value,
      }

      const pagination = {
        page: currentPage.value,
        limit: itemsPerPage.value,
      }

      const response = await api.getSuppliers(filters, sort, pagination, scope)

      if (!response.data || !response.data.items) {
        console.error('Некорректный ответ от API:', response)
        throw new Error('Некорректный ответ от сервера: нет списка поставщиков')
      }

      suppliers.value = response.data.items
      totalCount.value = response.data.pagination.total
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки поставщиков'
      console.error('Ошибка при загрузке поставщиков:', e)
    } finally {
      loading.value = false
    }
  }

  async function fetchSupplier(id: string) {
    loading.value = true
    error.value = null

    try {
      const api = supplierApi()
      currentSupplier.value = await api.getSupplier(id)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки поставщика'
      console.error('Ошибка при загрузке поставщика:', e)
    } finally {
      loading.value = false
    }
  }

  async function createSupplier(data: CreateSupplierData) {
    loading.value = true
    error.value = null

    try {
      const api = supplierApi()
      const newSupplier = await api.createSupplier(data)
      suppliers.value.unshift(newSupplier)
      return newSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка создания поставщика'
      console.error('Ошибка при создании поставщика:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateSupplier(id: string, data: UpdateSupplierData) {
    loading.value = true
    error.value = null

    try {
      const api = supplierApi()
      const updatedSupplier = await api.updateSupplier(id, data)

      // Обновляем в списке
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value[index] = updatedSupplier
      }

      // Обновляем текущего поставщика
      if (currentSupplier.value?.id === id) {
        currentSupplier.value = updatedSupplier
      }

      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка обновления поставщика'
      console.error('Ошибка при обновлении поставщика:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function deleteSupplier(id: string) {
    loading.value = true
    error.value = null

    try {
      const api = supplierApi()
      await api.deleteSupplier(id)

      // Удаляем из списка
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value.splice(index, 1)
      }

      // Сбрасываем текущего поставщика
      if (currentSupplier.value?.id === id) {
        currentSupplier.value = null
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка удаления поставщика'
      console.error('Ошибка при удалении поставщика:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function approveSupplier(id: string, notes?: string) {
    try {
      const api = supplierApi()
      const updatedSupplier = await api.approveSupplier(id, notes)

      // Обновляем в списке
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value[index] = updatedSupplier
      }

      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка одобрения поставщика'
      console.error('Ошибка при одобрении поставщика:', e)
      throw e
    }
  }

  async function suspendSupplier(id: string, reason: string) {
    try {
      const updatedSupplier = await supplierApi().suspendSupplier(id, reason)
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value[index] = updatedSupplier
      }

      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка приостановки поставщика'
      console.error('Ошибка при приостановке поставщика:', e)
      throw e
    }
  }

  async function activateSupplier(id: string) {
    try {
      const updatedSupplier = await supplierApi().activateSupplier(id)
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value[index] = updatedSupplier
      }

      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка активации поставщика'
      console.error('Ошибка при активации поставщика:', e)
      throw e
    }
  }

  async function rejectSupplier(id: string, reason: string) {
    try {
      const api = supplierApi()
      const updatedSupplier = await api.rejectSupplier(id, reason)

      // Обновляем в списке
      const index = suppliers.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        suppliers.value[index] = updatedSupplier
      }

      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка отклонения поставщика'
      console.error('Ошибка при отклонении поставщика:', e)
      throw e
    }
  }

  async function fetchSupplierStats() {
    try {
      const api = supplierApi()
      stats.value = await api.getSupplierStats()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки статистики'
      console.error('Ошибка при загрузке статистики:', e)
    }
  }

  async function fetchSupplierProducts(_supplierId: string) {
    try {
      const response = await supplierProductApi.getProducts({ search: '', limit: 100 })
      supplierProducts.value = (response as SupplierProduct[]) ?? []
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки товаров поставщика'
      console.error('Ошибка при загрузке товаров поставщика:', e)
    }
  }

  async function fetchSupplierUsers(supplierId: string) {
    try {
      const api = useSupplierUserAPI()
      supplierUsers.value = await api.getActiveSupplierUsers(supplierId)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки пользователей поставщика'
      console.error('Ошибка при загрузке пользователей поставщика:', e)
    }
  }

  // Утилиты для фильтрации и сортировки
  function setSearch(query: string) {
    searchQuery.value = query
    currentPage.value = 1
  }

  function setStatus(status: SupplierStatus | null) {
    selectedStatus.value = status
    currentPage.value = 1
  }

  function setCity(city: string | null) {
    selectedCity.value = city
    currentPage.value = 1
  }

  function setRating(rating: number | null) {
    minRating.value = rating
    currentPage.value = 1
  }

  function setFeaturedOnly(featured: boolean) {
    featuredOnly.value = featured
    currentPage.value = 1
  }

  function setSorting(sort: string, order: 'asc' | 'desc' = 'asc') {
    sortBy.value = sort
    sortOrder.value = order
    currentPage.value = 1
  }

  function setPage(page: number) {
    currentPage.value = page
  }

  function resetFilters() {
    searchQuery.value = ''
    selectedStatus.value = null
    selectedCity.value = null
    minRating.value = null
    featuredOnly.value = false
    currentPage.value = 1
  }

  function clearCurrentSupplier() {
    currentSupplier.value = null
    supplierProducts.value = []
    supplierUsers.value = []
  }

  return {
    // Состояние
    suppliers,
    currentSupplier,
    supplierProducts,
    supplierUsers,
    stats,
    loading,
    error,

    // Пагинация
    currentPage,
    itemsPerPage,
    totalCount,

    // Фильтры
    searchQuery,
    selectedStatus,
    selectedCity,
    minRating,
    featuredOnly,

    // Сортировка
    sortBy,
    sortOrder,

    // Геттеры
    filteredSuppliers,
    paginationInfo,
    pendingSuppliers,
    approvedSuppliers,
    activeSuppliers,
    featuredSuppliers,

    // Действия
    fetchSuppliers,
    fetchSupplier,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    approveSupplier,
    rejectSupplier,
    suspendSupplier,
    activateSupplier,
    fetchSupplierStats,
    fetchSupplierProducts,
    fetchSupplierUsers,

    // Утилиты
    setSearch,
    setStatus,
    setCity,
    setRating,
    setFeaturedOnly,
    setSorting,
    setPage,
    resetFilters,
    clearCurrentSupplier,
  }
})
